import { useState, useEffect } from 'react';
import {
  KINGDOMS,
  KingdomKey,
  KingdomsData,
  KingdomStats,
  TargetlistItem,
} from '../types/kingdoms';

export function useKingdomData() {
  const [kingdomData, setKingdomData] = useState<KingdomsData>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKingdoms = async () => {
      try {
        const res = await fetch('/api/Targetlist');
        const data = await res.json();
        const items: TargetlistItem[] = data.items;

        const getPercentages = (group: TargetlistItem[]): Omit<KingdomStats, 'displayName'> => {
          //display only established species for kingdom charts.
          const ESTABLISHED_STATUSES = ["1a", "1b", "2a", "2b"];
          const isEstablished = (i: TargetlistItem) => ESTABLISHED_STATUSES.includes(i.speciesOccurrenceStatus);

          const totalSpecies = group.filter(isEstablished).length;

          const totalOtherSpecies = group.filter(
            i => isEstablished(i) && i.otherBarcodes > 0
          ).length;

          const totalAllSpecies = group.filter(
            i => isEstablished(i) && i.allBarcodes > 0
          ).length;

          const totalAriseSpecies = group.filter(
            i => isEstablished(i) && (i.ariseBarcodes + i.collected > 0)
          ).length;

          const totalSharedSpecies = group.filter(
            i =>
              isEstablished(i) &&
              (i.ariseBarcodes + i.collected > 0) &&
              i.otherBarcodes > 0
          ).length;

          const uniqueAriseSpecies = group.filter(
            i =>
              isEstablished(i) &&
              (i.ariseBarcodes + i.collected > 0) &&
              i.otherBarcodes === 0
          ).length;


          return {
            arise: Math.round((totalAriseSpecies / totalSpecies) * 100),
            other: Math.round((totalOtherSpecies / totalSpecies) * 100),
            total: Math.round((totalAllSpecies / totalSpecies) * 100),
            shared: Math.round((totalSharedSpecies / totalSpecies) * 100),
            uniqueArisePercent: Math.round((uniqueAriseSpecies / totalSpecies) * 100),
            totalSpecies,
            totalAriseSpecies,
            totalOtherSpecies,
            totalSharedSpecies,
          };
        };

        const newKingdomData: KingdomsData = {};

        (Object.keys(KINGDOMS) as KingdomKey[]).forEach((key) => {
          const kingdomName = KINGDOMS[key];
          const filtered = items.filter(i => i.kingdom === kingdomName);
          newKingdomData[key] = {
            ...getPercentages(filtered),
            displayName: kingdomName,
          };
        });

        setKingdomData(newKingdomData);
      } catch (err) {
        console.error('Error fetching kingdom data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchKingdoms();
  }, []);

  return { kingdomData, loading };
}
